import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    Science as ScienceIcon,
    Person as PersonIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';

const OperatorDetailModal = ({ operator, open, onClose, onAddToExpression }) => {
    if (!operator) return null;

    const getOperatorTypeIcon = (type) => {
        switch(type) {
            case 'published_scale': return <SchoolIcon color="primary" />;
            case 'user_scale': return <PsychologyIcon color="secondary" />;
            default: return <ScienceIcon color="action" />;
        }
    };

    const formatItemText = (itemId, item) => {
        if (!operator.prompt_templates?.item_format) {
            return `${itemId}. ${item.text}`;
        }
        return operator.prompt_templates.item_format
            .replace('{item_id}', itemId)
            .replace('{text}', item.text);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getOperatorTypeIcon(operator.type)}
                    <Box>
                        <Typography variant="h5" component="div">
                            {operator.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip 
                                label={operator.type === 'published_scale' ? 'Published Scale' : 'User Scale'}
                                color={operator.type === 'published_scale' ? 'primary' : 'secondary'}
                                size="small"
                            />
                            <Chip 
                                label={operator.version ? `v${operator.version}` : 'v1.0'} 
                                size="small" 
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Basic Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ mr: 1 }} />
                        Overview
                    </Typography>
                    
                    <Typography variant="body1" paragraph>
                        {operator.description || 'No description available for this operator.'}
                    </Typography>

                    {operator.authors && operator.authors.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Authors:
                            </Typography>
                            <Typography variant="body2">
                                {operator.authors.join(', ')}
                            </Typography>
                        </Box>
                    )}

                    {operator.created_at && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Created:
                            </Typography>
                            <Typography variant="body2">
                                {new Date(operator.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Response Model Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <TimelineIcon sx={{ mr: 1 }} />
                        Response Model
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell>{operator.response_model?.type || 'Not specified'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Range</TableCell>
                                    <TableCell>
                                        {operator.response_model?.range ? 
                                            `${operator.response_model.range[0]} - ${operator.response_model.range[1]}` : 
                                            'Not specified'
                                        }
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Default Value</TableCell>
                                    <TableCell>{operator.response_model?.default_value || 'Not specified'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Total Items</TableCell>
                                    <TableCell>{Object.keys(operator.items || {}).length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Factors</TableCell>
                                    <TableCell>{operator.factors?.length || 0}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Factor Structure */}
                {operator.factors && operator.factors.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Factor Structure
                        </Typography>
                        
                        {operator.factors.map((factor, index) => (
                            <Accordion key={index} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2 }}>
                                            {factor.label}
                                        </Typography>
                                        <Chip 
                                            label={`${factor.items_included?.length || 0} items`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {factor.description || 'No description available for this factor.'}
                                    </Typography>
                                    
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Included Items:
                                    </Typography>
                                    
                                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                        {factor.items_included?.map((itemId) => {
                                            const item = operator.items?.[itemId];
                                            if (!item) return null;
                                            
                                            return (
                                                <Box key={itemId} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                    <Typography variant="body2">
                                                        {formatItemText(itemId, item)}
                                                    </Typography>
                                                    {item.reverse_coded && (
                                                        <Chip 
                                                            label="Reverse Coded" 
                                                            size="small" 
                                                            color="warning"
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        }) || (
                                            <Typography variant="body2" color="text.secondary">
                                                No items specified for this factor.
                                            </Typography>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                )}

                {/* Sample Items (if no factors, show all items) */}
                {(!operator.factors || operator.factors.length === 0) && operator.items && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Sample Items
                        </Typography>
                        
                        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {Object.entries(operator.items).slice(0, 10).map(([itemId, item]) => (
                                <Box key={itemId} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2">
                                        {formatItemText(itemId, item)}
                                    </Typography>
                                </Box>
                            ))}
                            {Object.keys(operator.items).length > 10 && (
                                <Typography variant="caption" color="text.secondary">
                                    ... and {Object.keys(operator.items).length - 10} more items
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Prompt Templates */}
                {operator.prompt_templates && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Prompt Templates
                        </Typography>
                        
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableBody>
                                    {Object.entries(operator.prompt_templates).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                                {key.replace(/_/g, ' ')}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {value}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
                <Button 
                    variant="contained" 
                    onClick={() => {
                        onAddToExpression(operator);
                        onClose();
                    }}
                >
                    Add to Identity
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OperatorDetailModal;
